import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import {
  ListRequest,
  useVbsWorkOrderListQuery,
} from "@/services/backend/vbs/work-orders/api/work-order-api";
import { useRltWorkOrderListQuery } from "@/services/backend/rlt/work-orders/api/work-order-api";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { RefreshCw } from "lucide-react";
import { ReactNode, useState } from "react";
import { CustomerName } from "@/services/backend/customers/components/customer-name";
import { BranchName } from "@/services/backend/branch/components/branch-name";
import { formatDate, formatTime } from "@/shared/lib/utilities/date";
import { NavLink } from "react-router";
import { useDocumentPath } from "@/services/backend/documents/components/use-document-path";
import { Button } from "@/shared/components/ui/button";
import { ServiceKindFilter } from "@/services/backend/services/components/service-kind-filter";
import { CompanyFilter } from "@/services/backend/company/components/company-filter";
import { BranchFilter } from "@/services/backend/branch/components/branch-filter";
import { ResetFilters } from "@/shared/components/filter/reset-filters";
import { Filter, FilterMode } from "@/shared/lib/filter/filter";
import { CustomerFilter } from "@/services/backend/customers/components/customer-filter";
import { DateBetweenFilter } from "@/shared/components/filter/date-between-filter";
import t from "@/lang/lang";
import { useDashboardContext } from "@/routes/dashboard/_components/dashboard-context";
import { Role } from "@/shared/lib/authorization/roles";
import { DocumentKind, ServiceKind } from "@bitory-gmbh/bsm";
import { FulfillmentStateFilter } from "@/services/backend/relationships/components/fulfillment-state-filter";
import { FulfillmentStateIcon } from "@/services/backend/relationships/components/fulfillment-state-icon";
import { FulfillmentState } from "@/services/backend/relationships/types/fulfillment";
import { GSMServiceKind } from "@/services/backend/services/types/kinds";
import { TableCard } from "@/shared/components/tables/table-card";

export function WorkOrdersTable() {
  const { role } = useDashboardContext();

  const [request, setRequest] = useState<ListRequest>(defaultRequest(role));
  const [kinds, setKinds] = useState<Filter<ServiceKind>>({
    active: false,
    values: [],
  });

  // This (calling each service) is an intermediate solution. In the future,
  // there should be a global document.workorder service that provides work
  // orders for all service kinds. This endpoint may also provide pagination.
  const {
    data: vbs,
    isLoading: vbsIsLoading,
    error: vbsError,
  } = useVbsWorkOrderListQuery(request, {
    skip: kinds.active && !kinds.values?.includes(GSMServiceKind.VBS),
  });
  const {
    data: rlt,
    isLoading: rltIsLoading,
    error: rltError,
  } = useRltWorkOrderListQuery(request, {
    skip: kinds.active && !kinds.values?.includes(GSMServiceKind.RLT),
  });

  const isLoading = vbsIsLoading || rltIsLoading;
  const error = vbsError || rltError;

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  const workOrders = [
    ...(vbs?.items ?? []).map((wo) => ({
      ...wo,
      serviceKind: GSMServiceKind.VBS,
    })),
    ...(rlt?.items ?? []).map((wo) => ({
      ...wo,
      serviceKind: GSMServiceKind.RLT,
    })),
  ].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));

  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <ServiceKindFilter
          filter={kinds}
          onFilterChange={(kind) => setKinds(kind)}
        />
        <FulfillmentStateFilter
          filter={request.state}
          onFilterChange={(state) => setRequest({ ...request, state })}
        />
        <CustomerFilter
          filter={request.customer}
          onFilterChange={(customer) => setRequest({ ...request, customer })}
        />
        <CompanyFilter
          filter={request.company}
          onFilterChange={(company) => setRequest({ ...request, company })}
        />
        <BranchFilter
          filter={request.branch}
          onFilterChange={(branch) => setRequest({ ...request, branch })}
        />
        <DateBetweenFilter
          label={t("Geändert Am")}
          filter={request.updatedAt!}
          onFilterChange={(updatedAt) => setRequest({ ...request, updatedAt })}
        />
        <ResetFilters onClick={() => setRequest(defaultRequest(role))} />
      </div>
      <TableCard>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nummer</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Kunde</TableHead>
              <TableHead>Niederlassung</TableHead>
              <TableHead>Geändert Am</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={5}>
                  <div className="text-muted-foreground flex items-center justify-center">
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Loading...
                  </div>
                </TableCell>
              </TableRow>
            )}
            {workOrders.map((workOrder) => (
              <TableRow key={workOrder.id}>
                <TableCell className="py-0 pl-1">
                  <WorkOrderLink
                    documentId={workOrder.id}
                    documentKind={workOrder.documentKind}
                    processId={workOrder.processId}
                    serviceKind={workOrder.serviceKind}
                  >
                    <Button variant="link" size="sm">
                      <DocumentNumberById documentId={workOrder.id} />
                    </Button>
                  </WorkOrderLink>
                </TableCell>
                <TableCell>
                  <FulfillmentStateIcon state={workOrder.state} />
                </TableCell>
                <TableCell>
                  <CustomerName customerId={workOrder.customerId} />
                </TableCell>
                <TableCell>
                  <BranchName branchId={workOrder.branchId} />
                </TableCell>
                <TableCell>
                  {formatDate(workOrder.updatedAt)},{" "}
                  {formatTime(workOrder.updatedAt)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableCard>
    </div>
  );
}

function WorkOrderLink({
  children,
  documentId,
  documentKind,
  processId,
  serviceKind,
}: {
  children: ReactNode;
  documentId: string;
  documentKind: DocumentKind;
  processId: string | null;
  serviceKind: ServiceKind;
}) {
  const { path } = useDocumentPath({
    documentId,
    documentKind,
    serviceKind,
    processId,
  });

  if (!path) {
    return null;
  }

  return <NavLink to={path}>{children}</NavLink>;
}

const defaultRequest = (role: Role): ListRequest => {
  const now = new Date();
  const from = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
  const to = new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString();

  return {
    process: {
      active: true,
      mode: FilterMode.NotNull,
    },
    customer: {
      active: false,
      values: [],
    },
    company: {
      active: false,
      values: [],
    },
    branch: {
      active: false,
      values: [],
    },
    state: {
      active: role === Role.TeamLeader,
      values: role === Role.TeamLeader ? [FulfillmentState.Execution] : [],
    },
    updatedAt: {
      active: true,
      mode: FilterMode.Between,
      values: [from, to],
    },
    limit: 50,
    offset: 0,
  };
};
